import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type { EvidenceAttachmentModel } from "@/features/cle-compliance/models/evidenceAttachment";
import type {
  TeachingEvidenceListModel,
  TeachingEvidenceModel,
  TeachingQueryParams,
} from "@/features/cle-compliance/models/teachingDocument";
import { del, get, getBlob, postForm } from "./util";

export const TEACHINGS_QUERY_KEY = ["teachings"] as const;

export const TEACHINGS_LIST_QUERY_KEY = [
  ...TEACHINGS_QUERY_KEY,
  "list",
] as const;

export const createTeachingDetailQueryKey = (id: string) =>
  [...TEACHINGS_QUERY_KEY, "detail", id] as const;

export const createTeachingFileQueryKey = (id: string) =>
  [...TEACHINGS_QUERY_KEY, "files", id] as const;

const TEACHINGS_QUERY = "documents/teachings";

const createTeachingDownloadQuery = (id: string): string =>
  `${TEACHINGS_QUERY}/${id}/download`;

const createTeachingsForReportingQuery = (reportId: string): string =>
  `${TEACHINGS_QUERY}/available-for-period/${reportId}`;

const createTeachingsForInjunctionQuery = (injunctionId: string): string =>
  `${TEACHINGS_QUERY}/available-for-injunction/${injunctionId}`;

const createTeachingsForImpositionQuery = (impositionId: string): string =>
  `${TEACHINGS_QUERY}/available-for-imposition/${impositionId}`;

const createSearchParams = ({
  page,
  page_size,
  reported,
}: TeachingQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (reported != null) {
    searchParams.set("reported", String(reported));
  }

  if (page != null) {
    searchParams.set("page", String(page));
  }

  if (page_size != null) {
    searchParams.set("page_size", String(page_size));
  }

  return searchParams.toString();
};

const createListQuery = (params: TeachingQueryParams = {}): string => {
  const queryString = createSearchParams(params);

  return queryString ? `${TEACHINGS_QUERY}?${queryString}` : TEACHINGS_QUERY;
};

export type CreateTeachingRequest = {
  file: File;
  numberOfLessons: number;
};

export const fetchTeachings = (
  params: TeachingQueryParams = {},
): Promise<PaginatedResponse<TeachingEvidenceListModel>> =>
  get<PaginatedResponse<TeachingEvidenceListModel>>({
    query: createListQuery(params),
  });

export const fetchTeachingsForReporting = (
  reportId: string,
): Promise<TeachingEvidenceListModel[]> =>
  get<TeachingEvidenceListModel[]>({
    query: createTeachingsForReportingQuery(reportId),
  });

export const fetchTeachingsForInjunction = (
  injunctionId: string,
): Promise<TeachingEvidenceListModel[]> =>
  get<TeachingEvidenceListModel[]>({
    query: createTeachingsForInjunctionQuery(injunctionId),
  });

export const fetchTeachingsForImposition = (
  impositionId: string,
): Promise<TeachingEvidenceListModel[]> =>
  get<TeachingEvidenceListModel[]>({
    query: createTeachingsForImpositionQuery(impositionId),
  });

export const fetchTeachingById = (id: string): Promise<TeachingEvidenceModel> =>
  get<TeachingEvidenceModel>({
    query: `${TEACHINGS_QUERY}/${id}`,
  });

export const createTeaching = ({
  file,
  numberOfLessons,
}: CreateTeachingRequest): Promise<TeachingEvidenceModel> => {
  const formData = new FormData();

  formData.append("file", file);

  return postForm<TeachingEvidenceModel>({
    query: `${TEACHINGS_QUERY}?numberOfLessons=${numberOfLessons}`,
    body: formData,
  });
};

export const fetchTeachingDocumentFile = (id: string): Promise<Blob> =>
  getBlob({
    query: createTeachingDownloadQuery(id),
  });

export const deleteTeaching = async (id: string): Promise<void> => {
  await del({
    query: `${TEACHINGS_QUERY}/${id}`,
  });
};

/* --- Supplerende dokumentation (bilag) ---------------------------------
 *
 * Samme bilag som på kursusbeviser (ADV-359). De ligger på undervisningens
 * detaljesvar, så der er ingen selvstændig liste-query at holde i sync. */

const createAttachmentsQuery = (teachingId: string): string =>
  `${TEACHINGS_QUERY}/${teachingId}/attachments`;

const createAttachmentQuery = (
  teachingId: string,
  attachmentId: string,
): string => `${createAttachmentsQuery(teachingId)}/${attachmentId}`;

export const uploadTeachingAttachment = (
  teachingId: string,
  file: File,
): Promise<EvidenceAttachmentModel> => {
  const formData = new FormData();
  formData.append("file", file);

  return postForm<EvidenceAttachmentModel>({
    query: createAttachmentsQuery(teachingId),
    body: formData,
  });
};

export const deleteTeachingAttachment = async (
  teachingId: string,
  attachmentId: string,
): Promise<void> => {
  await del({
    query: createAttachmentQuery(teachingId, attachmentId),
  });
};

export const fetchTeachingAttachmentFile = (
  teachingId: string,
  attachmentId: string,
): Promise<Blob> =>
  getBlob({
    query: `${createAttachmentQuery(teachingId, attachmentId)}/download`,
  });
